import {ChangeDetectionStrategy, Component, inject, input, OnInit, output} from '@angular/core';
import {MarketingBlockData, WebsiteBackupFile} from "../../types/base-marketing-block-types";
import {defaultMarketingBlockData} from "../../configs/default-marketing-block-data";
import {FormGroup} from "@angular/forms";
import {BaseMarketingBlockModalService} from "../../services/base-marketing-block-modal.service";
import {
  AttachProductCardFileParams,
  DeleteFileParams,
  GetFilesParams
} from "../../../../../types/product-card-files.types";
import {BaseMarketingBlockPathStateService} from "../../services/base-marketing-block-path-state.service";
import {LoadingService} from "../../../../../../../../core/services/loading.service";
import {AttachFileParams} from "../../../../../../../../core/types/file.types";
import {Subscription} from "rxjs";
import {MbFormComponent} from "../mb-form/mb-form.component";
import {FileCardComponent} from "../../../../../../../../shared/components/ui/file-card/file-card.component";
import {
  FileUploadCardComponent
} from "../../../../../../../../shared/components/ui/file-upload-card/file-upload-card.component";
import {LoaderComponent} from "../../../../../../../../shared/components/loader/loader.component";
import {MatFabButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";

@Component({
  selector: 'div[app-mb-editor]',
  standalone: true,
  imports: [
    MbFormComponent,
    FileCardComponent,
    FileUploadCardComponent,
    LoaderComponent,
    MatFabButton,
    MatIcon
  ],
  templateUrl: './mb-editor.component.html',
  styleUrl: './mb-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MbEditorComponent implements OnInit {
  marketingBlockData = input<MarketingBlockData>(defaultMarketingBlockData);
  marketingBlockFormGroup = input<FormGroup>(new FormGroup({}));
  parentId = input<number>(0);
  isEditModeActive = input<boolean>(false);
  columnKey = input<string>('');
  noFirstData = input<boolean>(false);
  isActiveEditor = input<boolean>(false);
  updateMarketingBlock = output<MarketingBlockData>();
  backClick = output<void>();

  private baseMarketingBlockModalService: BaseMarketingBlockModalService = inject(BaseMarketingBlockModalService);
  private baseMarketingBlockPathStateService: BaseMarketingBlockPathStateService = inject(BaseMarketingBlockPathStateService);
  private loadingService: LoadingService = inject(LoadingService);
  private subscriptions: Subscription = new Subscription();

  public websiteBackupFiles: Array<WebsiteBackupFile> = [];
  public isLoading: boolean = false;

  public async ngOnInit(): Promise<void> {
    if (this.columnKey() === 'websiteBackups') {
      this.initLoadingSubscriptions();
      await this.fetchWebsiteBackupFiles();
    }
  }

  public updateMarketingBlockData(marketingBlockData: MarketingBlockData): void {
    this.updateMarketingBlock.emit(marketingBlockData);
  }

  public handleBackClick(): void {
    this.backClick.emit();
  }

  public async handleDeleteFile(isImage: boolean, file: WebsiteBackupFile): Promise<void> {
    const deleteFileParams: DeleteFileParams = {
      path: this.baseMarketingBlockPathStateService.pathFileState(),
      id: file.id
    };

    this.baseMarketingBlockModalService.deleteFile(deleteFileParams)
      .then(() => this.websiteBackupFiles = this.websiteBackupFiles.filter(i => i.id !== file.id));
  }

  public async attachFile({ parentId, fileId }: AttachFileParams): Promise<void> {
    const attachFileParams: AttachProductCardFileParams = {
      path: this.baseMarketingBlockPathStateService.pathFileState(),
      body: {
        fileId: fileId,
        websiteBackupId: parentId
      }
    };

    await this.baseMarketingBlockModalService.attachFile<WebsiteBackupFile>(attachFileParams)
      .then((data) => this.websiteBackupFiles.push(data));
  }

  private async fetchWebsiteBackupFiles(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    const params: GetFilesParams = {
      path: this.baseMarketingBlockPathStateService.pathFileState(),
      params: { websiteBackupId: this.marketingBlockData().id }
    };

    this.websiteBackupFiles = await this.baseMarketingBlockModalService.getFiles<WebsiteBackupFile>(params)
      .catch(() => [])
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private initLoadingSubscriptions(): void {
    this.subscriptions.add(
      this.loadingService.loadingState$.subscribe({
        next: (isLoading: boolean) => this.isLoading = isLoading
      })
    );
  }
}
